import React, { PureComponent, Fragment } from "react";
import { Card, CardBody, Col, Modal } from "reactstrap";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import moment from "moment";
import OpenInNewIcon from "mdi-react/OpenInNewIcon";
import AlertIcon from "mdi-react/InformationOutlineIcon";

import FinancingsSearchTableHead from "./FinancingsSearchTableHead";
import LinearLoading from "../../../shared/components/LinearLoading";
import BuildingDetail from "../../BuildingDetail";
import {
	_capitalizeText,
	_formatNumber,
	_formatOwnerName,
} from "../../../shared/helpers/utils";

const DOC_TYPE_LABELS = {
	mtge: "Mortgage",
	agmt: "Agreement / Modification",
	asst: "Assignment",
	sat: "Satisfaction",
	cdec: "Consolidation",
	adec: "Assignment Declaration",
	"al&r": "Assignment of Leases & Rents",
	mcon: "Mortgage Consolidation",
	deed: "Deed",
};

const formatDocType = (doc) => {
	if (doc.doc_type_desc) return _capitalizeText(doc.doc_type_desc);
	if (doc.doc_type && DOC_TYPE_LABELS[doc.doc_type]) {
		return DOC_TYPE_LABELS[doc.doc_type];
	}
	return doc.doc_type ? doc.doc_type.toUpperCase() : "--";
};

const formatParty = (party) => {
	if (!party) return "--";
	if (Array.isArray(party)) return _formatOwnerName(party, true);
	return _formatOwnerName(String(party).split(/,(?=\S)/), true);
};

class FinancingsListTable extends PureComponent {
	state = { modal: false, trxnRes: null };

	_toggleModal = (doc) => {
		const { modal } = this.state;
		if (modal) {
			this.setState({ modal: false, trxnRes: null });
		} else {
			// Normalize the ACRIS row so BuildingDetail's lookup hits we3-buildings.
			// alternate_addresses on we3-buildings is lowercase; ACRIS full_street_address is uppercase.
			const trxnRes = doc
				? {
					...doc,
					alternate_addresses: doc.full_street_address
						? [doc.full_street_address.toLowerCase()]
						: undefined,
					display_full_street_address:
						doc.full_street_address || doc.display_full_street_address,
				}
				: null;
			this.setState({ modal: true, trxnRes });
		}
	};

	render() {
		const {
			tableData,
			order,
			orderBy,
			onRequestSort,
			fetchInProgress,
		} = this.props;

		if (fetchInProgress && (!tableData || tableData.length === 0)) {
			return <LinearLoading />;
		}

		if (!tableData || tableData.length === 0) {
			return (
				<Col md={12}>
					<Card>
						<CardBody className={`small`}>
							<div className={`email-confirmation`}>
								<div className="email-confirmation__icon">
									<AlertIcon size={50} color={`#D6BAE8`} />
								</div>
								<h3 className="email-confirmation__title">
									No Financings Found
								</h3>
								<p className="email-confirmation__sub"></p>
							</div>
						</CardBody>
					</Card>
				</Col>
			);
		}

		return (
			<Fragment>
				<Table className="material-table">
					<FinancingsSearchTableHead
						order={order}
						orderBy={orderBy}
						onRequestSort={onRequestSort}
					/>
					<TableBody>
						{tableData.map((d) => (
							<TableRow
								className="material-table__row"
								tabIndex={-1}
								key={d.id}
								onClick={() => this._toggleModal(d)}
								style={{ cursor: "pointer" }}
							>
								<TableCell
									className="material-table__cell material-table__cell-right nowrap"
									size="small"
								>
									{d.full_street_address ? (
										<div>
											<span>{_capitalizeText(d.full_street_address)}</span>
											<br />
											<span
												className="light-text small-text"
												style={{ color: "#858AA1" }}
											>
												{d.borough ? _capitalizeText(d.borough) : ""}
											</span>
										</div>
									) : (
										<span>--</span>
									)}
								</TableCell>
								<TableCell
									className="material-table__cell material-table__cell-right"
									size="small"
								>
									<div>{formatDocType(d)}</div>
									{d.doc_type && (
										<div className="light-text xsmall-text">
											{d.doc_type.toUpperCase()}
										</div>
									)}
								</TableCell>
								<TableCell
									className="material-table__cell material-table__cell-right"
									size="small"
								>
									<span>
										{d.document_date
											? moment(d.document_date.split("T")[0]).format("M/D/YY")
											: "--"}
									</span>
								</TableCell>
								<TableCell
									className="material-table__cell material-table__cell-right"
									size="small"
								>
									<span>
										{d.recorded_datetime
											? moment(d.recorded_datetime.split("T")[0]).format(
												"M/D/YY"
											)
											: "--"}
									</span>
								</TableCell>
								<TableCell
									className="material-table__cell material-table__cell-right"
									size="small"
								>
									<span>
										{d.document_amt > 0
											? "$" + _formatNumber(d.document_amt)
											: "--"}
									</span>
								</TableCell>
								<TableCell
									className="material-table__cell material-table__cell-right"
									size="small"
								>
									<div
										className="light-text small-text"
										style={{ width: 200, whiteSpace: "normal" }}
									>
										{formatParty(d.party_1)}
									</div>
								</TableCell>
								<TableCell
									className="material-table__cell material-table__cell-right"
									size="small"
								>
									<div
										className="light-text small-text"
										style={{ width: 200, whiteSpace: "normal" }}
									>
										{formatParty(d.party_2)}
									</div>
								</TableCell>
								<TableCell
									className="material-table__cell material-table__cell-right"
									size="small"
								>
									{d.document_id && !isNaN(d.document_id) ? (
										<a
											href={`https://a836-acris.nyc.gov/DS/DocumentSearch/DocumentDetail?doc_id=${d.document_id}`}
											className="link-text light-text small-text"
											target="_blank"
											rel="noopener noreferrer"
											onClick={(e) => e.stopPropagation()}
										>
											ACRIS{" "}
											<OpenInNewIcon
												size={12}
												style={{ marginTop: -2 }}
											/>
										</a>
									) : (
										<span className="light-text small-text">--</span>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				<Modal
					isOpen={this.state.modal}
					toggle={() => this._toggleModal()}
					modalClassName={`ltr-support`}
					className={`modal-dialog contact__modal`}
				>
					<div className="modal__header">
						<button
							className="lnr lnr-cross modal__close-btn"
							type="button"
							onClick={() => this._toggleModal()}
						/>
					</div>
					<div className="modal__body">
						<BuildingDetail
							trxnRes={this.state.trxnRes}
							defaultTab={"financing"}
							isShare
						/>
					</div>
				</Modal>
			</Fragment>
		);
	}
}

export default FinancingsListTable;
